/**
 * Shimmer loading placeholders.
 *
 * Divergence from the usual vendoring pattern: upstream has **no** Skeleton
 * component. The listing loading state is inline screen markup at
 * `Dens N Dente Tailwind.dc.html:627-650`, styled by a `.dnd-skel` CSS class
 * declared in that file's own <style> block (`:36-37`) rather than in
 * tw-components.jsx. This module extracts that markup so the two listing
 * components share one copy of it. Every bar height, width, padding and gap
 * below is the handoff's value verbatim.
 *
 * BEHAVIOR.md is the spec for when it shows: "Page change: scroll to top + 450ms
 * skeleton state (shimmer cards matching the active view mode, `aria-busy`),
 * pager dims to 60% and ignores input, then the new grid fades up (`dndRevealUp`
 * 380ms `--ease-out`)." Shimmer loop is 1.2s linear.
 *
 * Note on `className`: `.dnd-skel` is injected into <head> after the app's
 * stylesheet, so at equal specificity it beats Tailwind utilities. That's
 * deliberate — the shimmer gradient must win — but it means you cannot recolour a
 * Skeleton with `bg-*`. Size and radius utilities work fine.
 */
import { cx } from "./cx";
import "./keyframes";

/** One shimmering block. Size and shape it with utilities. */
export function Skeleton({ className = "", style, ...rest }) {
  return (
    <div
      aria-hidden="true"
      className={cx("dnd-skel", className)}
      style={style}
      {...rest}
    />
  );
}

/**
 * Grid-card placeholder — `Dens N Dente Tailwind.dc.html:629-637`.
 *
 * `mediaClassName` defaults to the handoff's `h-[148px]`, which is right for its
 * own compact card. Pass the height of whatever card this stands in for; a
 * skeleton that doesn't match the real card's geometry shifts the layout on swap.
 */
export function ProductCardSkeleton({
  mediaClassName = "h-[148px]",
  className = "",
  style,
}) {
  // flex-col + the foot's mt-auto: when a caller stretches this card to the real
  // card's measured height (`ProductsSkeleton`'s `cardMinHeight`), the extra space
  // opens between the text lines and the foot — where the real card's whitespace
  // lives — instead of pooling under the foot.
  return (
    <div
      className={cx(
        "bg-card border border-line-subtle shadow-sm rounded-lg overflow-hidden flex flex-col",
        className
      )}
      style={style}
    >
      <Skeleton className={mediaClassName} />
      <div className="py-[14px] px-[14px] flex flex-col gap-[10px]">
        <Skeleton className="h-[10px] w-[40%] rounded-pill" />
        <Skeleton className="h-[12px] w-[92%] rounded-pill" />
        <Skeleton className="h-[12px] w-[68%] rounded-pill" />
        <Skeleton className="h-[20px] w-[52%] rounded-pill mt-[6px]" />
      </div>
      {/* The listing card's stepper + add-button row, as grey stand-ins. */}
      <div className="mt-auto flex items-center justify-between gap-[10px] px-[14px] pb-[14px]">
        <Skeleton className="h-[31px] w-[96px] rounded-pill" />
        <Skeleton className="h-[36px] flex-1 max-w-[45%] rounded-pill" />
      </div>
    </div>
  );
}

/**
 * List-row placeholder matching the live 90px `ProductRow`: 64px thumb + 12px
 * vertical padding on each side + two 1px borders. The handoff's placeholder is
 * shorter than its live row; matching the live geometry prevents loading reflow.
 * Rows are individually bordered cards in a gapped column, so the skeleton carries
 * the border by default. Pass `bordered={false}` when the rows sit inside a single
 * bordered container with dividers instead.
 */
export function ProductRowSkeleton({
  thumbClassName = "w-[64px] h-[64px]",
  bordered = true,
  className = "",
}) {
  return (
    <div
      className={cx(
        "flex items-center gap-[var(--space-4)] py-[var(--space-3)] px-[var(--space-5)]",
        bordered && "bg-card border border-line-subtle shadow-sm rounded-lg",
        className
      )}
    >
      <Skeleton className={cx("shrink-0 rounded-md", thumbClassName)} />
      <div className="flex-1 flex flex-col gap-[var(--space-2)]">
        <Skeleton className="h-[12px] w-[55%] rounded-pill" />
        <Skeleton className="h-[10px] w-[32%] rounded-pill" />
      </div>
      <Skeleton className="h-[16px] w-[72px] rounded-pill" />
    </div>
  );
}
